/**
 * @brisa/ui · LegalDrawer
 *
 * Drawer lateral controlado (slide from right desktop / bottom-sheet mobile)
 * que mostra Termos ou Privacidade SEM perder progresso da tela de consent.
 *
 * Estados:
 *   - closed: invisível
 *   - opening/open: overlay + drawer com focus trap
 *   - closing: dismiss via ✕, click overlay, ou Esc
 *
 * Não desmarca checkboxes ao fechar — usuário pode ler, fechar, e marcar.
 */
import * as React from 'react';

export interface LegalDrawerProps {
  document: 'tos' | 'privacy';
  version: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locale?: 'pt' | 'es';
  title?: string;
  closeLabel?: string;
  children: React.ReactNode;
}

export function LegalDrawer({
  document,
  version,
  open,
  onOpenChange,
  locale = 'pt',
  title,
  closeLabel = 'Fechar',
  children,
}: LegalDrawerProps) {
  void locale; // prop reservada para futura tradução do título por locale
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);
  const heading =
    title ?? (document === 'tos' ? 'Termos de Uso' : 'Política de Privacidade');

  React.useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    closeBtnRef.current?.focus();
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className="brisa-legal-drawer__overlay"
      onClick={() => onOpenChange(false)}
      role="presentation"
    >
      <aside
        className="brisa-legal-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="brisa-legal-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="brisa-legal-drawer__header">
          <h2 id="brisa-legal-drawer-title" className="brisa-display brisa-legal-drawer__title">
            {heading} · {version}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="brisa-legal-drawer__close"
            aria-label={closeLabel}
            onClick={() => onOpenChange(false)}
          >
            ✕
          </button>
        </header>
        <div className="brisa-legal-drawer__body">{children}</div>
      </aside>
    </div>
  );
}
